<template>
  <v-card outlined class="ma-0 mb-4">
    <v-col>
      <v-row class="ma-0">
        <v-col
          v-if="author"
          class="pa-0 flex-grow-0 d-flex align-center"
        >
          <researcher-avatar :researcher="author" small />
        </v-col>
        <v-col class="pa-0 pl-2">
          <div class="body-2 font-weight-bold">
            {{ author ? author.name : 'Deleted user' }}
          </div>
          <div class="grey--text caption">
            {{ formattedTimestamp(comment.createdAt) }}
            <v-tooltip v-if="isEdited" bottom>
              <template #activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">(edited)</span>
              </template>
              This comment was edited
              {{ formattedTimestamp(comment.updatedAt) }}
            </v-tooltip>
          </div>
        </v-col>
        <v-row v-if="currentUserIsAuthor" class="flex-grow-0">
          <v-btn small icon>
            <v-icon
              color="grey"
              class="material-icons-outlined"
              @click="onEdit"
            >
              edit
            </v-icon>
          </v-btn>
          <v-btn small icon class="mr-2">
            <v-icon
              color="grey"
              class="material-icons-outlined"
              @click="requestDelete"
            >
              delete
            </v-icon>
          </v-btn>
        </v-row>
        <v-overlay
          :absolute="true"
          opacity="0.8"
          :value="deleteRequested"
          @click:outside="onCancel"
        >
          <v-col class="align-center">
            <p class="text-center mb-2">Delete this comment?</p>
            <v-row class="justify-center">
              <action-button small class="mr-2" @click="onCancel">
                Cancel
              </action-button>
              <action-button small color="error" @click="onDelete">
                Delete
              </action-button>
            </v-row>
          </v-col>
        </v-overlay>
      </v-row>

      <comment-textarea
        v-if="editing"
        v-model="modifiedCommentText"
        :saving="saving"
        placeholder="Edit comment"
        :comment-text="comment.text"
        class="mt-2"
        @onSave="onSave"
        @onCancel="onCancel"
      />
      <p
        v-else
        class="whitespace-pre body-2 font-weight-light my-0 mt-2"
      >
        {{ comment.text }}
      </p>
    </v-col>
  </v-card>
</template>

<script>
import { Comment } from '@/models';
import ResearcherAvatar from '@/components/Common/ResearcherAvatar';
import ActionButton from '@/components/Common/ActionButton';
import CommentTextarea from './CommentTextarea';
import moment from 'moment';
import { mapActions } from 'vuex';

export default {
  name: 'CommentItem',
  components: { ResearcherAvatar, ActionButton, CommentTextarea },
  props: {
    comment: {
      type: Comment,
      required: true,
    },
    currentUserIsAuthor: {
      type: Boolean,
      required: true,
    },
  },
  data: function () {
    return {
      editing: false,
      saving: false,
      deleteRequested: false,
      editFocused: false,
      modifiedCommentText: this.comment.text,
    };
  },
  computed: {
    isEdited() {
      return this.comment.updatedAt !== this.comment.createdAt;
    },
    // Null when the comment's author has been deleted (reviewer FK set to null).
    author() {
      return this.comment.reviewer?.researcher ?? null;
    },
  },
  methods: {
    ...mapActions({
      updateComment: 'comment/update',
      deleteComment: 'comment/delete',
    }),
    formattedTimestamp(dateTime) {
      return 'at ' + moment(dateTime).format('h:mma on MMM D');
    },
    onCancel() {
      this.editing = false;
      this.deleteRequested = false;
    },
    async onEdit() {
      this.editing = true;
    },
    async onSave(modifiedCommentText) {
      this.saving = true;
      await this.updateComment({
        commentId: this.comment.id,
        text: modifiedCommentText,
      });
      this.editing = false;
      this.saving = false;
    },
    requestDelete() {
      this.deleteRequested = true;
    },
    async onDelete() {
      await this.deleteComment({
        id: this.comment.id,
        referenceId: this.comment.reference.id,
      });
      this.deleteRequested = false;
    },
  },
};
</script>
<style scoped>
.whitespace-pre {
  white-space: pre-wrap;
  word-wrap: break-word;
}
</style>
